import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Checkbox } from 'shared/components/ui/checkbox'
import { Label } from 'shared/components/ui/label'
import { Skeleton } from 'shared/components/ui/skeleton'
import { useDataTable } from 'shared/hooks/use-data-table'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * The batches at the hub's door — the Manifests addressed to the hub, newest
 * first (spec §12; ADR-0026). The hub worker picks one and receives it on the
 * checklist. A closed batch is history, shown only when asked.
 */
type HubManifestRow = RouterOutputs['hubReceiving']['Manifests'][number]

const NUMBER = {
	headClassName: 'text-right',
	cellClassName: 'text-right tabular-nums',
}

/** A count, muted when nothing is there to see. */
function Count({ value, warn = false }: { value: number; warn?: boolean }) {
	return (
		<span
			className={cn(
				value === 0 && 'text-muted-foreground',
				warn && value > 0 && 'font-semibold text-amber-700 dark:text-amber-400',
			)}
		>
			{value}
		</span>
	)
}

export function HubManifests({
	onOpen,
}: {
	onOpen: (manifestId: string) => void
}) {
	const { t } = useTranslate()
	const [includeClosed, setIncludeClosed] = useState(false)
	const query = useQuery(
		trpc.hubReceiving.Manifests.queryOptions({ includeClosed }),
	)
	const items = query.data ?? []

	const columns = useMemo<ColumnDef<HubManifestRow>[]>(
		() => [
			{
				id: 'batch',
				header: t('hub.colBatch'),
				cell: ({ row: { original: m } }) => (
					<div className='flex flex-col'>
						<span className='font-medium'>{m.dispatchRef}</span>
						<span className='text-xs text-muted-foreground'>
							{t('hub.revision', { revision: String(m.revision) })}
						</span>
					</div>
				),
			},
			{
				id: 'partner',
				header: t('hub.partner'),
				cell: ({ row }) => row.original.partnerName,
			},
			{
				id: 'state',
				header: t('hub.colState'),
				cell: ({ row: { original: m } }) => (
					<Badge
						variant={m.state === 'IN_RECEIVING' ? 'default' : 'secondary'}
						data-state={m.state}
					>
						{t(`manifestState.${m.state}`)}
					</Badge>
				),
			},
			{
				id: 'received',
				header: t('hub.colReceived'),
				meta: NUMBER,
				cell: ({ row: { original: m } }) => (
					<span>
						{m.counts.received}
						<span className='text-muted-foreground'>
							{' '}
							/ {m.counts.expected}
						</span>
					</span>
				),
			},
			{
				id: 'extra',
				header: t('hub.colExtra'),
				meta: NUMBER,
				cell: ({ row }) => <Count value={row.original.counts.extra} warn />,
			},
			{
				id: 'problems',
				header: t('hub.colProblems'),
				meta: NUMBER,
				cell: ({ row }) => <Count value={row.original.counts.problems} warn />,
			},
			{
				id: 'announced',
				header: t('hub.colAnnounced'),
				cell: ({ row }) => (
					<span className='text-muted-foreground'>
						{formatDayTime(row.original.createdAt)}
					</span>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) => (
					<Button
						size='sm'
						variant='ghost'
						className='underline-offset-4 hover:underline'
						data-testid={`hub-manifest-open-${row.original.manifestId}`}
						onClick={(event) => {
							event.stopPropagation()
							onOpen(row.original.manifestId)
						}}
					>
						{t('hub.open')}
						<ChevronRight />
					</Button>
				),
			},
		],
		[t, onOpen],
	)

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (m) => m.manifestId,
		initialState: { pagination: { pageIndex: 0, pageSize: 200 } },
	})

	return (
		<div className='flex flex-col gap-3'>
			<div className='flex items-center justify-end gap-2'>
				<Checkbox
					id='hub-include-closed'
					data-testid='hub-include-closed'
					checked={includeClosed}
					onCheckedChange={(checked) => setIncludeClosed(checked === true)}
				/>
				<Label htmlFor='hub-include-closed' className='font-normal'>
					{t('hub.showClosed')}
				</Label>
			</div>
			{query.isLoading ? (
				<div className='flex flex-col gap-2'>
					<Skeleton className='h-10 w-full' />
					<Skeleton className='h-10 w-full' />
					<Skeleton className='h-10 w-full' />
				</div>
			) : query.isError ? (
				<p
					className='text-sm text-destructive'
					data-testid='hub-manifests-error'
				>
					{t('hub.loadFailed', { message: query.error.message })}
				</p>
			) : (
				<DataTable
					table={table}
					withPagination={false}
					emptyText={
						<span data-testid='hub-manifests-empty'>
							{t('hub.manifestsEmpty')}
						</span>
					}
					row={{
						attributes: (m) => ({
							'data-testid': `hub-manifest-${m.manifestId}`,
						}),
						onRowClick: (m) => onOpen(m.manifestId),
					}}
				/>
			)}
		</div>
	)
}
